export const capitalizeFirst = (str = "") =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

/**
 * Returns a reusable MUI `sx` style object to color the underline of an `<Input>`
 * component based on whether an error is present.
 *
 * The underline color changes according to the Material UI theme:
 *     - `error.main` when `hasError` is true
 *     - default Material underline colors otherwise
 *
 * @param {boolean} hasError - Whether the input field currently has a validation error.
 * @returns {object} An `sx` style object to apply to the MUI `<Input>` component.
 *
 * @example
 *     <Input
 *         sx={[
 *             underlineErrorStyle(Boolean(errors.clientDailyIntake)),
 *             { fontSize: '1rem' }
 *         ]}
 *     />
 */

export const underlineErrorStyle = (hasError) => ({
    // Underline (normal)
    '&:before': {
        borderBottomColor: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.42)',
    },
    // Underline on hover
    '&:hover:not(.Mui-disabled):before': {
        borderBottomColor: hasError ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
    },
    // Underline on focus
    '&:after': {
        borderBottomColor: hasError ? 'error.main' : 'primary.main',
    },
    // InputAdornment color
    '& .MuiInputAdornment-root': {
        color: hasError ? 'error.main' : 'text.secondary',
    },
});

/**
 * Reusable MUI `sx` style object for an outlined `<Select>` component
 * styled to match the app's CSS variable colour scheme.
 *
 * Applies to: border colour (default / hover / focused) and dropdown icon colour.
 */
export const muiSelectSx = {
    height: 40,
    backgroundColor: 'var(--background-color)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--primary-color)' },
    '& .MuiSelect-icon': { color: 'var(--primary-color)' },
};

/**
 * Reusable MUI `sx` style object for `<MenuItem>` components
 * inside a `<Select>` styled to match the app's CSS variable colour scheme.
 *
 * Applies to: hover, selected, and selected+hover background colours.
 */
export const muiMenuItemSx = {
    '&:hover': { backgroundColor: 'var(--tertiary-color)' },
    '&.Mui-selected': { backgroundColor: 'var(--secondary-color)' },
    '&.Mui-selected:hover': { backgroundColor: 'var(--secondary-color)' },
};

/**
 * Reusable MUI `sx` style object for a `<DatePicker>` component
 * styled to match the app's CSS variable colour scheme.
 *
 * Applies to: border colour (default / hover / focused), label colour,
 * calendar icon colour, and day selection highlight.
 */
export const muiDatePickerSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'var(--background-color)',
        '& fieldset': { borderColor: '#ccc' },
        '&:hover fieldset': { borderColor: 'var(--secondary-color)' },
        '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
    },
    '& .MuiInputLabel-root': { color: '#555' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
    '& .MuiSvgIcon-root': { color: 'var(--primary-color)' },
};

export const datePickerErrorStyle = (hasError, themeColors = { focus: 'orange', normal: 'black' }) => ({
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: hasError ? 'error.main' : themeColors.normal,
        },
        "&:hover fieldset": {
            borderColor: hasError ? 'error.main' : themeColors.normal,
        },
        "&.Mui-focused fieldset": {
            borderColor: hasError ? 'error.main' : themeColors.focus,
        },
    },
    "& .MuiInputLabel-root": {
        color: hasError ? 'error.main' : themeColors.normal,
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: hasError ? 'error.main' : themeColors.focus,
    },
    "& .MuiSvgIcon-root": {
        color: hasError ? 'error.main' : themeColors.normal,
    },
});

/**
 * OWASP-aligned password rules.
 * Each entry carries a `test` predicate and the i18n key for its hint label.
 */
export const PASSWORD_RULES = [
    { key: 'minLength', test: (p) => p.length >= 8,          i18nKey: 'errorMessages.pwd-rule-min-length'  },
    { key: 'uppercase', test: (p) => /[A-Z]/.test(p),        i18nKey: 'errorMessages.pwd-rule-uppercase'   },
    { key: 'number',    test: (p) => /[0-9]/.test(p),        i18nKey: 'errorMessages.pwd-rule-number'     },
    { key: 'special',   test: (p) => /[^A-Za-z0-9]/.test(p), i18nKey: 'errorMessages.pwd-rule-special'    },
];

/**
 * Returns an object mapping each rule key to a boolean (true = passes).
 * @param {string} password
 * @returns {{ minLength: boolean, uppercase: boolean, number: boolean, special: boolean }}
 */
export const validatePasswordStrength = (password) =>
    Object.fromEntries(PASSWORD_RULES.map(({ key, test }) => [key, test(password ?? '')]));

/**
 * Returns true only if every PASSWORD_RULES predicate passes.
 * @param {string} password
 * @returns {boolean}
 */
export const isPasswordStrong = (password) =>
    PASSWORD_RULES.every(({ test }) => test(password ?? ''));

/**
 * Returns the URL unchanged if it uses a safe protocol (http:, https:,
 * a root-relative path starting with '/', or a client-generated blob: URL).
 * Returns null for any other scheme (e.g. javascript:, data:, vbscript:).
 *
 * Use this wherever a server-supplied URL is rendered in href, src, or iframe src.
 *
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export const sanitizeUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    if (url.startsWith('/') || url.startsWith('blob:')) return url;
    try {
        const { protocol } = new URL(url);
        return (protocol === 'http:' || protocol === 'https:') ? url : null;
    } catch {
        return null;
    }
};
